"use client";

import type { VitrineDemo, VitrineInlineEdit } from "@/lib/vitrine/types";
import { useTrackEvent } from "@/components/vitrine/demo/use-track-event";
import { DemoForm } from "@/components/vitrine/demo/demo-form";
import { DemoEstimator } from "@/components/vitrine/demo/demo-estimator";
import { DemoMap } from "@/components/vitrine/demo/demo-map";
import { InlineEditDemo } from "@/components/vitrine/demo/inline-edit";
import { BeforeAfter } from "@/components/vitrine/demo/before-after";

// Section "démonstration" de la page vitrine : la fonctionnalité custom qui
// FONCTIONNE (LANDING_VITRINE.md §2 et §5), présentée dans un cadre "aperçu
// de votre futur site". Ne reçoit jamais de `generatedPrompt`.

const HEADINGS: Record<VitrineDemo["component"], string> = {
  form: "Votre formulaire fonctionne déjà",
  estimator: "Votre estimateur fonctionne déjà",
  map: "Votre annuaire fonctionne déjà",
};

function BrowserFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-line bg-paper-pure shadow-sm">
      <div className="flex items-center gap-1.5 border-b border-line bg-paper-alt px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
        <span className="ml-3 text-xs text-ink/50">{label}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Leur future page d'accueil, juste après le hero : la preuve
 * d'exécution la plus parlante (décision 2026-09-27). En refonte, avec la
 * capture de leur site actuel : curseur avant / après.
 */
export function MaquetteSection({
  mockupSrc,
  beforeSrc,
  slug,
  track,
  lang = "fr",
  prospect = false,
}: {
  mockupSrc: string;
  beforeSrc: string | null;
  slug: string;
  track: boolean;
  lang?: "fr" | "en";
  prospect?: boolean;
}) {
  const send = useTrackEvent(slug, track);
  const en = lang === "en";
  // Prospection : l'entreprise n'a publié aucune annonce.
  const source = prospect ? (en ? "your current website" : "votre site actuel") : en ? "your posting" : "votre annonce";
  return (
    <div className="mx-auto max-w-5xl px-4">
      <h2 className="text-center font-display text-4xl font-normal text-ink sm:text-5xl">
        {en
          ? beforeSrc
            ? "Your site, before and after"
            : "Your future home page"
          : beforeSrc
            ? "Votre site, avant et après"
            : "Votre future page d'accueil"}
      </h2>
      <p className="mx-auto mt-3 max-w-2xl text-center text-ink/70">
        {en
          ? beforeSrc
            ? `Your current home page, and the mockup of the one I propose, built from ${source}.`
            : `Built from ${source}, before we have even spoken. We rework it together at the mockup stage.`
          : beforeSrc
            ? `Votre page d'accueil actuelle, et la maquette de celle que je vous propose, construite à partir de ${source}.`
            : `Construite à partir de ${source}, avant même notre premier échange. On la retravaille ensemble à l'étape de la maquette.`}
      </p>
      <div className="editorial-rule mx-auto mt-6" />
      <BrowserFrame label={en ? "Preview of your future site" : "Aperçu de votre futur site"}>
        {beforeSrc ? (
          <div className="p-5 sm:p-8">
            <BeforeAfter
              beforeSrc={beforeSrc}
              afterSrc={mockupSrc}
              onInteract={() => send("demo_interacted")}
              onComplete={() => send("demo_completed")}
              lang={lang}
            />
          </div>
        ) : (
          // Maquette pleine page (souvent 6 000 px et plus) : cadre défilant.
          <div
            className="max-h-[760px] overflow-y-auto"
            onScroll={() => send("demo_interacted")}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={mockupSrc}
              alt={en ? "Mockup of your future home page" : "Maquette de votre future page d'accueil"}
              className="w-full"
            />
          </div>
        )}
      </BrowserFrame>
    </div>
  );
}

export function DemoSection({
  demo,
  slug,
  track,
}: {
  demo: VitrineDemo;
  slug: string;
  track: boolean;
}) {
  const send = useTrackEvent(slug, track);
  const handlers = {
    onInteract: () => send("demo_interacted"),
    onComplete: () => send("demo_completed"),
  };

  return (
    <div className="mx-auto max-w-4xl px-4">
      <h2 className="text-center font-display text-4xl font-normal text-ink sm:text-5xl">
        {HEADINGS[demo.component]}
      </h2>
      <p className="mx-auto mt-3 max-w-2xl text-center text-ink/70">
        Pas une maquette : essayez-le. C&apos;est une première version de ce que votre site proposera à vos
        clients, construite à partir de votre annonce.
      </p>
      <div className="editorial-rule mx-auto mt-6" />
      <BrowserFrame label="Aperçu de votre futur site">
        <div className="p-5 sm:p-8">
          {demo.component === "form" && <DemoForm config={demo.config} {...handlers} />}
          {demo.component === "estimator" && <DemoEstimator config={demo.config} {...handlers} />}
          {demo.component === "map" && <DemoMap config={demo.config} {...handlers} />}
        </div>
      </BrowserFrame>
    </div>
  );
}

export function InlineEditSection({
  content,
  slug,
  track,
  lang = "fr",
}: {
  content: VitrineInlineEdit;
  slug: string;
  track: boolean;
  lang?: "fr" | "en";
}) {
  const send = useTrackEvent(slug, track);
  return <InlineEditDemo content={content} onEdit={() => send("inline_edited")} lang={lang} />;
}
